import React, { useState, useEffect } from 'react';
import { BillData, Company } from '../types/billing';

interface BillEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (bill: BillData) => Promise<void>;
  bill: BillData | null;
  companies: Company[];
}

const BillEditModal: React.FC<BillEditModalProps> = ({
  isOpen,
  onClose,
  onSave,
  bill,
  companies
}) => {
  const [formData, setFormData] = useState<BillData>({
    companyName: '',
    companySubtitle: '',
    companyAddress: '',
    companyPhone: '',
    companyEmail: '',
    companyPAN:'',
    companyGSTN: '',
    invoiceNumber: '',
    invoiceDate: '',
    workOrderNumber: '',
    workOrderDate: '',
    clientId: '',
    clientName: '',
    clientAddress: '',
    clientGSTN: '',
    clientState: '',
    clientStateCode: '',
    clientEmail: '',
    clientCompanyName: '',
    items: [],
    gstRate: 18,
    igstAmount: 0,
    subTotal: 0,
    cgstAmount: 0,
    sgstAmount: 0,
    totalGstAmount: 0,
    totalAmount: 0,
    bankDetails: {
      bankName: '',
      branch: '',
      accountNumber: '',
      ifscCode: ''
    },
    termsAndConditions: [],
    status: 'Sent',
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString()
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (bill) {
      setFormData(bill);
    } else {
      // Reset form for new bill
      setFormData({
        companyName: '',
        companySubtitle: '',
        companyAddress: '',
        companyPhone: '',
        companyEmail: '',
        companyPAN:'',
        companyGSTN: '',
        invoiceNumber: '',
        invoiceDate: '',
        workOrderNumber: '',
        workOrderDate: '',
        clientId: '',
        clientName: '',
        clientAddress: '',
        clientGSTN: '',
        clientState: '',
        clientStateCode: '',
        clientEmail: '',
        clientCompanyName: '',
        items: [],
        gstRate: 18,
        igstAmount: 0,
        subTotal: 0,
        cgstAmount: 0,
        sgstAmount: 0,
        totalGstAmount: 0,
        totalAmount: 0,
        bankDetails: {
          bankName: '',
          branch: '',
          accountNumber: '',
          ifscCode: ''
        },
        termsAndConditions: [],
        status: 'Sent',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      });
    }
  }, [bill]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    
    if (name === 'clientId') {
      const selectedCompany = companies.find(c => c.id === value);
      setFormData(prev => ({
        ...prev,
        [name]: value,
        clientName: selectedCompany?.name || prev.clientName,
        clientAddress: selectedCompany?.address || prev.clientAddress,
        clientGSTN: selectedCompany?.gstn || prev.clientGSTN,
        clientState: selectedCompany?.state || prev.clientState,
        clientStateCode: selectedCompany?.stateCode || prev.clientStateCode
      }));
    } else if (name.startsWith('bankDetails.')) {
      const field = name.split('.')[1] as keyof typeof formData.bankDetails;
      setFormData(prev => ({
        ...prev,
        bankDetails: {
          ...prev.bankDetails,
          [field]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'totalAmount' || name === 'gstRate' || name === 'subTotal' || name === 'cgstAmount' || name === 'sgstAmount' || name === 'totalGstAmount' ? parseFloat(value) || 0 : value
      }));
    }
  };

  // Round amount to 2 decimal places for intermediate calculations
  const roundAmount = (num: number): number => {
    return Math.round(num * 100) / 100;
  };

  // Round to whole number (same as bill PDF - >= 0.50 rounds up, < 0.50 rounds down)
  const roundToWhole = (num: number): number => {
    return Math.round(num);
  };

  const handleItemChange = (index: number, field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) => {
        if (i === index) {
          const updatedItem = { ...item, [field]: value };
          // Recalculate totalAmount if qty or rate changed
          if (field === 'qty' || field === 'rate') {
            updatedItem.totalAmount = roundAmount((updatedItem.qty || 0) * (updatedItem.rate || 0));
          }
          return updatedItem;
        }
        return item;
      })
    }));
  };

  const addItem = () => {
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { 
        srNo: prev.items.length + 1, 
        description: '', 
        sac: '', 
        qty: 1, 
        rate: 0, 
        totalAmount: 0 
      }]
    }));
  };

  const removeItem = (index: number) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  const calculateTotal = () => {
    const subTotal = roundAmount(formData.items.reduce((sum, item) => sum + roundAmount(item.totalAmount || 0), 0));
    const totalGstAmount = roundAmount(subTotal * (formData.gstRate || 0));
    const cgstAmount = roundAmount(totalGstAmount / 2);
    const sgstAmount = roundAmount(totalGstAmount / 2);
    const totalBeforeRound = subTotal + totalGstAmount;
    // Round final total to whole number (same as bill PDF)
    const totalAmount = roundToWhole(totalBeforeRound);
    return { subTotal, cgstAmount, sgstAmount, totalGstAmount, totalAmount };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    
    try {
      const totals = calculateTotal();
      const billToSave = {
        ...formData,
        ...totals
      };
      await onSave(billToSave);
      onClose();
    } catch (error) {
      console.error('Error saving bill:', error);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-6xl w-full max-h-[95vh] overflow-y-auto">
        {/* Header with gradient background */}
        <div className="bg-gradient-to-r from-avis-blue to-avis-dark text-white p-6 rounded-t-xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-3xl font-bold">
                {bill ? 'Edit Bill' : 'Create New Bill'}
              </h2>
              <p className="text-blue-100 mt-1">
                {bill ? `Invoice #${bill.invoiceNumber}` : 'Fill in the details below to create a new bill'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:text-blue-200 text-3xl font-light transition-colors"
            >
              ×
            </button>
          </div>
        </div>

        <div className="p-8">

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information Section */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center mb-6">
                <div className="bg-avis-blue text-white p-2 rounded-lg mr-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Bill Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Invoice Number *
                </label>
                <input
                  type="text"
                  name="invoiceNumber"
                  value={formData.invoiceNumber}
                  onChange={handleInputChange}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                  placeholder="Enter invoice number"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Client *
                </label>
                <select
                  name="clientId"
                  value={formData.clientId}
                  onChange={handleInputChange}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                  required
                >
                  <option value="">Select Client</option>
                  {companies.map(company => (
                    <option key={company.id} value={company.id}>
                      {company.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Invoice Date *
                </label>
                <input
                  type="date"
                  name="invoiceDate"
                  value={formData.invoiceDate}
                  onChange={handleInputChange}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Work Order Number
                </label>
                <input
                  type="text"
                  name="workOrderNumber"
                  value={formData.workOrderNumber}
                  onChange={handleInputChange}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                  placeholder="Enter work order number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Work Order Date
                </label>
                <input
                  type="text"
                  name="workOrderDate"
                  value={formData.workOrderDate}
                  onChange={handleInputChange}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                  placeholder="Enter date or N/A"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Period Of Work
                </label>
                <input
                  type="text"
                  name="periodOfWork"
                  value={formData.periodOfWork || ''}
                  onChange={handleInputChange}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                  placeholder="e.g., Nov-25"
                />
              </div>
              

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Status *
                </label>
                <select
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                  className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors text-gray-900"
                  required
                >
                  <option value="Draft">Draft</option>
                  <option value="Sent">Sent</option>
                  <option value="Paid">Paid</option>
                </select>
              </div>
              </div>
            </div>

            {/* Items Section */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex justify-between items-center mb-6">
                <div className="flex items-center">
                  <div className="bg-green-500 text-white p-2 rounded-lg mr-3">
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                    </svg>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900">Bill Items</h3>
                </div>
                <button
                  type="button"
                  onClick={addItem}
                  className="bg-avis-blue text-white px-6 py-3 rounded-lg hover:bg-avis-dark transition-colors font-medium flex items-center space-x-2 shadow-lg"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Add Item</span>
                </button>
              </div>

              {formData.items.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border-2 border-dashed border-gray-200">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                  </svg>
                  <h3 className="mt-2 text-sm font-medium text-gray-900">No items added</h3>
                  <p className="mt-1 text-sm text-gray-500">Get started by adding your first bill item.</p>
                  <div className="mt-6">
                    <button
                      type="button"
                      onClick={addItem}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-avis-blue hover:bg-avis-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-avis-blue"
                    >
                      <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Add First Item
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.items.map((item, index) => (
                  <div key={index} className="bg-white rounded-lg p-6 border-2 border-gray-100 shadow-sm hover:shadow-md transition-shadow">
                    <div className="grid grid-cols-1 md:grid-cols-6 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Sr. No.
                        </label>
                        <input
                          type="number"
                          value={item.srNo}
                          onChange={(e) => handleItemChange(index, 'srNo', parseInt(e.target.value) || 0)}
                          className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors"
                          min="1"
                        />
                      </div>

                      <div className="md:col-span-2">
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Description
                        </label>
                        <input
                          type="text"
                          value={item.description}
                          onChange={(e) => handleItemChange(index, 'description', e.target.value)}
                          className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors"
                          placeholder="Item description"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          SAC Code
                        </label>
                        <input
                          type="text"
                          value={item.sac}
                          onChange={(e) => handleItemChange(index, 'sac', e.target.value)}
                          className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors"
                          placeholder="SAC code"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Quantity
                        </label>
                        <input
                          type="number"
                          value={item.qty}
                          onChange={(e) => {
                            const qty = parseFloat(e.target.value) || 0;
                            const totalAmount = qty * item.rate;
                            handleItemChange(index, 'qty', qty);
                            handleItemChange(index, 'totalAmount', totalAmount);
                          }}
                          className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">
                          Rate (₹)
                        </label>
                        <input
                          type="number"
                          value={item.rate}
                          onChange={(e) => {
                            const rate = parseFloat(e.target.value) || 0;
                            const totalAmount = item.qty * rate;
                            handleItemChange(index, 'rate', rate);
                            handleItemChange(index, 'totalAmount', totalAmount);
                          }}
                          className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors"
                          min="0"
                          step="0.01"
                        />
                      </div>

                      <div className="flex items-end">
                        <div className="flex-1">
                          <label className="block text-sm font-semibold text-gray-700 mb-2">
                            Amount (₹)
                          </label>
                          <div className="flex">
                            <input
                              type="number"
                              value={item.totalAmount}
                              onChange={(e) => handleItemChange(index, 'totalAmount', parseFloat(e.target.value) || 0)}
                              className="w-full border-2 border-gray-200 rounded-lg px-3 py-2 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors"
                              min="0"
                              step="0.01"
                            />
                            <button
                              type="button"
                              onClick={() => removeItem(index)}
                              className="ml-3 bg-red-100 text-red-600 hover:bg-red-200 px-3 py-2 rounded-lg transition-colors"
                              title="Remove item"
                            >
                              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
                </div>
              )}

              <div className="mt-6 bg-avis-blue text-white rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold">Total Amount:</span>
                  <span className="text-2xl font-bold">₹{calculateTotal().toLocaleString()}</span>
                </div>
              </div>
            </div>

            {/* Client Information Section */}
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-center mb-6">
                <div className="bg-purple-500 text-white p-2 rounded-lg mr-3">
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <h3 className="text-xl font-semibold text-gray-900">Client Information</h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Client Address
                  </label>
                  <textarea
                    name="clientAddress"
                    value={formData.clientAddress}
                    onChange={handleInputChange}
                    rows={4}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors resize-none"
                    placeholder="Enter client address..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Client GSTN
                  </label>
                  <input
                    type="text"
                    name="clientGSTN"
                    value={formData.clientGSTN}
                    onChange={handleInputChange}
                    className="w-full border-2 border-gray-200 rounded-lg px-4 py-3 focus:ring-2 focus:ring-avis-blue focus:border-avis-blue transition-colors"
                    placeholder="Enter client GST number..."
                  />
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="flex justify-end space-x-4 pt-8 border-t-2 border-gray-100">
              <button
                type="button"
                onClick={onClose}
                className="px-8 py-3 border-2 border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-all duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className="px-8 py-3 bg-gradient-to-r from-avis-blue to-avis-dark text-white rounded-lg hover:from-avis-dark hover:to-avis-blue transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed font-medium shadow-lg hover:shadow-xl"
              >
                {isLoading ? (
                  <div className="flex items-center space-x-2">
                    <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Saving...</span>
                  </div>
                ) : (
                  <div className="flex items-center space-x-2">
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>Save Bill</span>
                  </div>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default BillEditModal;
